import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Sparkles, Compass, ArrowRight } from 'lucide-react';
import { membershipPlans } from '../mockData';
import '../styles/Membership.css';
import Footer from '../components/Footer';
import UniversalCTA from '../components/UniversalCTA';
import SEO from '../components/SEO';
import LuxeCompassWatermark from '../components/LuxeCompassWatermark';
import ServiceTransformCards from '../components/ServiceTransformCards';

const Membership = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);

  return (
    <div className="membership-page">
      <SEO
        title="Membership — Ongoing Patient Advocacy"
        description="CADENCE membership plans for patients who need ongoing clinical advocacy, second opinions, and care coordination. Annual access to your healthcare partner."
        path="/membership"
      />
      {/* STANDARDIZED: Emblem section - same as other pages */}
      <section className="emblem-section" data-testid="membership-emblem">
        <div className="container">
          <div className="emblem-wrapper">
            <img src="/cadence-gold-foil.png" alt="CADENCE Medical Consulting" className="velvet-emblem-home" />
          </div>
        </div>
      </section>

      <section className="unified-content-section">
        <div className="container">
          <LuxeCompassWatermark />
          <h1 className="unified-page-title">Membership Plans</h1>
        </div>
      </section>
      
      <section className="unified-quote-section">
        <div className="container">
          <p className="unified-page-quote" style={{ fontStyle: 'normal' }}>
            Keep a medical consultant on retainer for ongoing support, priority access, and peace of mind.
          </p>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="value-prop-section">
        <div className="container">
          <div className="value-prop-content">
            <h2 className="section-title-white">What Comes Next After Your Diagnostic Package?</h2>
            <p className="body-text-white">
              Our diagnostic packages give you the 'what' and 'why' behind your health. But what comes next? Managing a complex health journey alone can feel overwhelming.
            </p>
            <p className="body-text-white">
              Our membership tiers provide the <strong>continuous partnership, strategy, and advocacy</strong> you need to implement your plan with confidence, prevent setbacks, and ensure you're always moving forward with expert guidance in your corner.
            </p>
            <div className="cta-intro">
              <Link to="/contact" className="btn-gold">
                Request a 15-Minute Membership Intro Call
              </Link>
              <p className="body-small-white" style={{ marginTop: '1rem' }}>
                Discover which tier aligns with your next chapter
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Membership Plans */}
      <section className="plans-section">
        <div className="container">
          <h2 className="heading-1" style={{ textAlign: 'center', marginBottom: '3rem' }}>Choose Your Plan</h2>
          <div className="plans-grid">
            {membershipPlans.map((plan) => {
              return (
              <div key={plan.id} className={`membership-card ${plan.featured ? 'featured' : ''} ${selectedPlan === plan.id ? 'selected' : ''}`}>
                {plan.featured && (
                  <div className="featured-tag">
                    <Sparkles size={16} />
                    <span>Best Value</span>
                  </div>
                )}
                <div className="plan-header">
                  <div className="tier-icon-wrapper">
                    <img src="/cadence-logo-gold-bg.png" alt="CADENCE" className="tier-emblem-img" />
                  </div>
                  <h3 className="heading-2">{plan.name}</h3>
                  <p className="plan-tagline">{plan.tagline}</p>
                  <div className="plan-pricing">
                    <span className="plan-price">${plan.price}</span>
                    <span className="plan-interval">/ {plan.interval}</span>
                  </div>
                  <p className="plan-target">{plan.targetClient}</p>
                  <p className="body-medium plan-description">{plan.description}</p>
                </div>
                <ul className="plan-features">
                  {plan.features.map((feature, idx) => (
                    <li key={idx}>
                      <CheckCircle size={20} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                {plan.footnote && (
                  <p className="plan-footnote">{plan.footnote}</p>
                )}
                <Link
                  to="/portal"
                  className={`btn-portal-cta ${plan.featured ? 'btn-primary' : 'btn-secondary'}`}
                  data-testid={`select-plan-${plan.id}`}
                >
                  Start Your Free Intake
                </Link>
              </div>
              );
            })}
          </div>

          {selectedPlan && (
            <div className="signup-form-section">
              <div className="signup-form-container">
                <h3 className="heading-2" style={{ textAlign: 'center', marginBottom: '1.5rem' }}>Complete Your Membership</h3>
                <form className="membership-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>First Name *</label>
                      <input type="text" placeholder="John" required />
                    </div>
                    <div className="form-group">
                      <label>Last Name *</label>
                      <input type="text" placeholder="Doe" required />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Email Address *</label>
                    <input type="email" placeholder="john.doe@example.com" required />
                    </div>
                  <div className="form-group">
                    <label>Phone Number *</label>
                    <input type="tel" placeholder="(555) 123-4567" required />
                  </div>
                  <div className="form-group">
                    <label>Create Password *</label>
                    <input type="password" placeholder="Minimum 8 characters" required />
                  </div>
                  <div className="form-group">
                    <label>Primary Health Concern (Optional)</label>
                    <textarea placeholder="Brief description of your main health concerns..." rows="3"></textarea>
                  </div>
                  <div className="form-checkbox">
                    <input type="checkbox" id="terms" required />
                    <label htmlFor="terms">I agree to the <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link></label>
                  </div>
                  <div className="form-checkbox">
                    <input type="checkbox" id="hipaa" required />
                    <label htmlFor="hipaa">I acknowledge the <Link to="/hipaa">HIPAA Privacy Notice</Link></label>
                  </div>
                  <button type="submit" className="btn-primary" style={{ width: '100%', padding: '1.25rem' }}>
                    Complete Membership Signup
                  </button>
                  <p className="body-small" style={{ textAlign: 'center', marginTop: '1rem', color: '#4A0E4E' }}>
                    You'll be redirected to our secure payment processor to complete your subscription.
                  </p>
                </form>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* The Difference, Service by Service — auto-expanded comparison table.
          Moved here from CadencePromise per user 2026-05-24 — sits directly
          below the 3 membership plum tiles so visitors can compare what's
          included tier-by-tier without scrolling away. */}
      <section className="difference-table-section" data-testid="difference-table-section">
        <div className="container">
          <ServiceTransformCards defaultExpanded />
        </div>
      </section>

      {/* FAQs consolidated to the unified /faq page 2026-05-19 per user.
          Direct link kept here for convenience. */}
      <section className="faq-section" id="faq">
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="heading-2" style={{ marginBottom: '1rem' }}>Have Questions?</h2>
          <p className="body-large" style={{ marginBottom: '1.5rem' }}>
            Cancellations, strategy calls, à la carte access, and tier upgrades &mdash; all answered in one place.
          </p>
          <Link to="/faq#membership" className="btn-gold" data-testid="membership-view-faqs">
            View All Membership FAQs
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* CADENCE Compass Link Card - COPPER (SOLUTION) - After FAQs */}
      <section className="compass-link-section">
        <div className="container">
          <div className="copper-compass-card">
            <Compass size={36} className="copper-compass-icon" />
            <div className="copper-compass-content">
              <h3 className="copper-compass-heading">Explore More in CADENCE Compass</h3>
              <p className="copper-compass-text">Discover interactive guides, treatment pathways, and educational resources to help you understand your health journey.</p>
              <Link to="/articles" className="btn-copper-outline" style={{ color: '#2D0A31' }}>
                Visit CADENCE Compass <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="heading-2" style={{ color: 'white' }}>Still Have Questions?</h2>
            <p className="body-large" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              Contact us to learn more about how membership can support your healthcare journey.
            </p>
            <Link to="/contact" className="btn-gold">
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <UniversalCTA />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Membership;